// src/pages/Procurement/POTimelineLogPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/axios";
import POTimelineLog from "@/components/Procurement/POTimelineLog";
import { Loader2, Search } from "lucide-react";

/* utils */
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
const poNum = (po) => po.po_number || `PO-${String(po.po_id).padStart(5, "0")}`;

function StatusPill({ status }) {
  const s = String(status || "").toLowerCase();
  const tone =
    s.includes("approved") ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" :
    s.includes("pending")  ? "bg-amber-500/15 text-amber-300 border-amber-500/30" :
    s.includes("partially")? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" :
    s.includes("fully")    ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" :
                             "bg-slate-500/15 text-slate-300 border-slate-500/30";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${tone}`}>
      {status || "—"}
    </span>
  );
}

function PoCard({ po, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative group text-left"
      title="View timeline"
    >
      {/* glow */}
      <div
        className={`absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-cyan-500/40 via-fuchsia-500/40 to-purple-500/40 opacity-0 blur transition-opacity duration-300 group-hover:opacity-60 ${
          selected ? "opacity-80" : ""
        }`}
        aria-hidden
      />
      {/* card */}
      <div
        className={`relative rounded-2xl bg-[#0c1126]/90 border p-4 transition-colors ${
          selected ? "border-cyan-400/60" : "border-white/10 hover:border-cyan-300/40"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-white">{poNum(po)}</div>
          <StatusPill status={po.status} />
        </div>
        <div className="mt-1 text-sm text-slate-300 line-clamp-2">
          {po.vendor_name || "—"}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-slate-400">
          <div>
            <div className="opacity-70">Created</div>
            <div className="text-slate-200">{fmtDate(po.created_at)}</div>
          </div>
          <div>
            <div className="opacity-70">Expected delivery</div>
            <div className="text-slate-200">{fmtDate(po.expected_delivery)}</div>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function POTimelineLogPage() {
  const [params, setParams] = useSearchParams();
  const initialId = Number(params.get("poId")) || null;

  const [poId, setPoId] = useState(initialId);
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const timelineRef = useRef(null);

  /* load POs once */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/purchase-orders/all");
        setList(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setList([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* reflect selection in URL */
  useEffect(() => {
    const p = new URLSearchParams(params);
    if (poId) p.set("poId", String(poId));
    else p.delete("poId");
    setParams(p, { replace: true });
  }, [poId]); // eslint-disable-line

  /* filter list by search */
  const options = useMemo(() => {
    const k = q.trim().toLowerCase();
    const filtered = !k
      ? list
      : list.filter((r) =>
          [r.po_number, r.po_id, r.vendor_name, r.status]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(k))
        );
    return filtered.slice(0, 48);
  }, [list, q]);

  const onSelect = (id) => {
    setPoId(id);
    // scroll timeline into view for a tighter flow
    setTimeout(() => timelineRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 10);
  };

  const selectedPO = useMemo(() => list.find((r) => r.po_id === poId) || null, [list, poId]);

  return (
    <div className="min-h-screen px-6 md:px-10 py-8 bg-gradient-to-br from-[#0a0f24] to-[#1e2240] text-white">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">PO Timeline & Activity</h1>
            <p className="text-slate-300 text-sm mt-1">
              Click a PO card to view its full activity log.
            </p>
          </div>
          <Link
            to="/procurement"
            className="px-3 py-2 rounded border border-white/20 bg-white/10 hover:bg-white/15"
          >
            ← Procurement
          </Link>
        </header>

        {/* Finder / Cards */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-3">
            <Search className="w-4 h-4 opacity-80" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search PO #, vendor or status…"
              className="flex-1 bg-[#0e1530] border border-white/15 rounded-lg px-3 py-2 outline-none"
            />
          </div>

          {loading ? (
            <div className="py-8 text-center opacity-80">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Loading POs…
            </div>
          ) : options.length === 0 ? (
            <div className="py-8 text-center text-sm opacity-80">No matching POs.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {options.map((po) => (
                <PoCard
                  key={po.po_id}
                  po={po}
                  selected={po.po_id === poId}
                  onClick={() => onSelect(po.po_id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Actions for selected */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-300">
            {selectedPO ? (
              <>
                Selected: <span className="text-white font-semibold">{poNum(selectedPO)}</span>{" "}
                — {selectedPO.vendor_name || "—"}
              </>
            ) : (
              "Pick a PO to see its timeline."
            )}
          </div>
          <div className="flex items-center gap-2">
            {poId ? (
              <>
                <Link
                  to={`/procurement/po/${poId}/receive?mode=view&log=1`}
                  className="inline-flex items-center justify-center rounded-lg bg-white/10 border border-white/15 px-3 py-2 hover:bg-white/15 text-sm"
                >
                  Open in Receiving (View)
                </Link>
                <a
                  href={`${import.meta.env.VITE_API_BASE || "/solo-mdsh-api"}/purchase-orders/pdf/${poId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-lg bg-white/10 border border-white/15 px-3 py-2 hover:bg-white/15 text-sm"
                >
                  View PDF
                </a>
              </>
            ) : null}
          </div>
        </div>

        {/* Timeline */}
        <div ref={timelineRef} className="bg-white/5 border border-white/10 rounded-2xl p-4">
          {!poId ? (
            <div className="text-sm opacity-80">Pick a PO to see its timeline.</div>
          ) : (
            <POTimelineLog poId={poId} />
          )}
        </div>
      </div>
    </div>
  );
}
